import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AttachFile as AttachIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { chequeAttachmentService } from '../../../services/cheques/chequeAttachmentService';
import { documentTypeService } from '../../../services/documentTypes/documentTypeService';
import { useEasyCheck } from '../../../context/EasyCheckContext';
import { canAccess } from '../../../utils/planPermissions';

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(2.5),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  },
}));

const AttachmentCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1, 1.5),
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    backgroundColor: theme.palette.grey[50],
  },
}));

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const ALLOWED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.bmp', '.pdf', '.doc', '.docx', '.xls', '.xlsx'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const getFileIcon = (contentType) => {
  if (!contentType) return <FileIcon />;
  if (contentType.startsWith('image/')) return <ImageIcon color="primary" />;
  if (contentType === 'application/pdf') return <PdfIcon color="error" />;
  return <FileIcon color="action" />;
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${sizes[i]}`;
};

const ChequeAttachments = ({ chequeId, readOnly = false, compact = false, onError, onSuccess }) => {
  const { user } = useEasyCheck();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedDocTypeId, setSelectedDocTypeId] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);

  // Fetch attachments
  const { data: attachments = [], isLoading: isLoadingAttachments } = useQuery({
    queryKey: ['chequeAttachments', chequeId],
    queryFn: async () => {
      const result = await chequeAttachmentService.getAttachments(chequeId);
      return result.data || [];
    },
    enabled: !!chequeId,
  });

  // Fetch document types
  const { data: documentTypes = [] } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: async () => {
      const result = await documentTypeService.getAll();
      return result.data || [];
    },
  });

  // Fetch storage usage
  const { data: storageUsage } = useQuery({
    queryKey: ['companyStorage'],
    queryFn: async () => {
      const result = await chequeAttachmentService.getStorageUsage();
      return result.data;
    },
  });

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: ({ file, documentTypeId }) =>
      chequeAttachmentService.uploadAttachment(chequeId, file, documentTypeId),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['chequeAttachments', chequeId] });
        queryClient.invalidateQueries({ queryKey: ['companyStorage'] });
        onSuccess?.('Attachment uploaded successfully');
      } else {
        onError?.(result.message || 'Upload failed');
      }
    },
    onError: (error) => {
      const msg = error?.response?.data?.Message || error?.message || 'Upload failed';
      onError?.(msg);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => chequeAttachmentService.deleteAttachment(id),
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['chequeAttachments', chequeId] });
        queryClient.invalidateQueries({ queryKey: ['companyStorage'] });
        onSuccess?.('Attachment deleted');
      } else {
        onError?.(result.message || 'Delete failed');
      }
      setDeleteConfirmId(null);
    },
    onError: () => {
      onError?.('Delete failed');
      setDeleteConfirmId(null);
    },
  });

  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      onError?.(`Invalid file type: ${ext}. Allowed: PNG, JPEG, TIFF, BMP, PDF, DOC, DOCX, XLS, XLSX`);
      return false;
    }
    if (file.size > MAX_FILE_SIZE) {
      onError?.('File size exceeds 10MB limit');
      return false;
    }
    return true;
  };

  const handleUpload = (file) => {
    if (!selectedDocTypeId) {
      onError?.('Please select a document type first');
      return;
    }
    if (!validateFile(file)) return;
    uploadMutation.mutate({ file, documentTypeId: selectedDocTypeId });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleUpload(file);
  };

  const isViewable = (contentType) => {
    if (!contentType) return false;
    return contentType.startsWith('image/') || contentType === 'application/pdf';
  };

  const handleView = async (attachmentId) => {
    const newWindow = window.open('', '_blank');
    try {
      const result = await chequeAttachmentService.downloadAttachment(attachmentId);
      if (result.success && result.data?.url) {
        newWindow.location.href = result.data.url;
      } else {
        newWindow.close();
        onError?.('Failed to generate view link');
      }
    } catch {
      newWindow?.close();
      onError?.('Failed to view attachment');
    }
  };

  const handleDownload = async (attachmentId, fileName) => {
    const newWindow = window.open('', '_blank');
    try {
      const result = await chequeAttachmentService.downloadAttachment(attachmentId);
      if (result.success && result.data?.url) {
        if (result.data.isBlob) {
          const a = newWindow.document.createElement('a');
          a.href = result.data.url;
          a.download = fileName;
          newWindow.document.body.appendChild(a);
          a.click();
          setTimeout(() => newWindow.close(), 1000);
        } else {
          newWindow.location.href = result.data.url;
        }
      } else {
        newWindow.close();
        onError?.('Failed to generate download link');
      }
    } catch {
      newWindow?.close();
      onError?.('Failed to download attachment');
    }
  };

  const handleDelete = (id) => {
    if (deleteConfirmId === id) {
      deleteMutation.mutate(id);
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  if (!chequeId) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        Save the cheque first to attach documents.
      </Alert>
    );
  }

  // Plan-based access check - hide attachments for non-Premium users
  if (!canAccess(user?.planId, 'documentAttachments')) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Document attachments are available on the Premium plan.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: compact ? 1 : 2 }}>
      {/* Storage Usage */}
      {storageUsage && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary">
              Storage: {storageUsage.UsedFormatted || storageUsage.usedFormatted} / {storageUsage.LimitFormatted || storageUsage.limitFormatted}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(storageUsage.UsagePercent || storageUsage.usagePercent || 0).toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(storageUsage.UsagePercent || storageUsage.usagePercent || 0, 100)}
            color={(storageUsage.UsagePercent || storageUsage.usagePercent || 0) > 90 ? 'error' : 'primary'}
            sx={{ borderRadius: 1, height: 6 }}
          />
        </Box>
      )}

      {/* Existing Attachments */}
      {isLoadingAttachments ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : attachments.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
          {attachments.map((att) => {
            const id = att.Id || att.id;
            const fileName = att.FileName || att.fileName;
            const contentType = att.ContentType || att.contentType;
            const fileSize = att.OriginalFileSize || att.originalFileSize;
            const docTypeName = att.DocumentTypeName || att.documentTypeName;
            const docTypeCode = att.DocumentTypeCode || att.documentTypeCode;
            const uploadedBy = att.UploadedBy || att.uploadedBy;
            return (
              <AttachmentCard key={id}>
                {getFileIcon(contentType)}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                    {fileName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={docTypeCode || docTypeName} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatBytes(fileSize)}
                    </Typography>
                    {!compact && uploadedBy && (
                      <Typography variant="caption" color="text.secondary">
                        by {uploadedBy}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {isViewable(contentType) && (
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleView(id)}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Download">
                  <IconButton size="small" onClick={() => handleDownload(id, fileName)}>
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!readOnly && (
                  <Tooltip title={deleteConfirmId === id ? 'Click again to confirm' : 'Delete'}>
                    <IconButton
                      size="small"
                      color={deleteConfirmId === id ? 'error' : 'default'}
                      onClick={() => handleDelete(id)}
                      disabled={deleteMutation.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </AttachmentCard>
            );
          })}
        </Box>
      ) : (
        !readOnly && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No attachments yet.
          </Typography>
        )
      )}

      {/* Upload Section */}
      {!readOnly && (
        <>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Document Type</InputLabel>
            <Select
              value={selectedDocTypeId}
              label="Document Type"
              onChange={(e) => setSelectedDocTypeId(e.target.value)}
            >
              {documentTypes.map((dt) => {
                const id = dt.Id || dt.id;
                const name = dt.TypeName || dt.typeName;
                const code = dt.TypeCode || dt.typeCode;
                return (
                  <MenuItem key={id} value={id}>
                    {code} — {name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          <UploadArea
            isDragOver={isDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => selectedDocTypeId && fileInputRef.current?.click()}
            sx={{ opacity: selectedDocTypeId ? 1 : 0.5 }}
          >
            {uploadMutation.isPending ? (
              <CircularProgress size={24} />
            ) : (
              <>
                <UploadIcon sx={{ fontSize: 32, color: 'primary.main', mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedDocTypeId
                    ? 'Drag & drop a file here, or click to browse'
                    : 'Select a document type above first'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Max 10MB — PNG, JPEG, PDF, DOC, XLS
                </Typography>
              </>
            )}
          </UploadArea>
          <VisuallyHiddenInput
            ref={fileInputRef}
            type="file"
            accept={ALLOWED_EXTENSIONS.join(',')}
            onChange={handleFileChange}
          />
        </>
      )}
    </Box>
  );
};

export default ChequeAttachments;
